import React from "react";
import { Link } from "react-router-dom";
import { Car, MessageCircle, FileCheck, KeyRound, ShieldCheck, Shield, ChevronRight } from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";
import CallButton from "@/components/CallButton";
import { waMessages } from "@/lib/whatsapp";

export function Breadcrumbs({ items }) {
  return (
    <nav aria-label="Breadcrumb" className="container-x py-4">
      <ol className="flex flex-wrap items-center gap-1 text-sm text-zinc-500">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-1">
            {it.to ? (
              <Link to={it.to} className="hover:text-black">{it.label}</Link>
            ) : (
              <span className="font-semibold text-zinc-800">{it.label}</span>
            )}
            {i < items.length - 1 && <ChevronRight className="h-4 w-4" />}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function HowItWorks() {
  const steps = [
    { icon: Car, title: "Choose Your Car", text: "Browse currently available Manchester PHV vehicles and pick one that suits you." },
    { icon: MessageCircle, title: "WhatsApp Us", text: "Message us about the vehicle you want. It's the fastest way to reach us." },
    { icon: FileCheck, title: "Send Your Details", text: "We confirm the required licence and insurance information with you." },
    { icon: KeyRound, title: "Arrange Collection", text: "Once everything's agreed, arrange collection and get on the road." },
  ];
  return (
    <section className="container-x py-20">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Simple Process</p>
      <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
        Rent a Manchester PHV in 4 Simple Steps
      </h2>
      <div className="mt-12 grid gap-6 md:grid-cols-4">
        {steps.map((s, i) => (
          <div key={i} className="border border-border p-6 transition-colors hover:border-zinc-900">
            <div className="flex items-center justify-between">
              <s.icon className="h-8 w-8 text-[#00E676]" />
              <span className="font-heading text-4xl font-black text-zinc-100">{i + 1}</span>
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold">{s.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-600">{s.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function InsuranceSection() {
  return (
    <section className="grain bg-charcoal py-20 text-white">
      <div className="container-x">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00E676]">Flexible Options</p>
        <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
          PHV Rental With or Without Insurance
        </h2>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <div className="border border-white/15 p-8">
            <Shield className="h-9 w-9 text-white" />
            <h3 className="mt-4 font-heading text-2xl font-bold">Without Insurance</h3>
            <p className="mt-3 leading-relaxed text-zinc-300">
              For drivers who already hold suitable private hire insurance, or who will arrange
              appropriate cover themselves. You rent the vehicle only.
            </p>
            <div className="mt-6">
              <WhatsAppButton message={waMessages.withoutInsurance()} testid="insurance-without-whatsapp">
                Ask About Vehicle-Only Rental
              </WhatsAppButton>
            </div>
          </div>
          <div className="border border-[#00E676]/40 bg-[#00E676]/5 p-8">
            <ShieldCheck className="h-9 w-9 text-[#00E676]" />
            <h3 className="mt-4 font-heading text-2xl font-bold">With Insurance</h3>
            <p className="mt-3 leading-relaxed text-zinc-300">
              Insurance-inclusive packages may be available subject to eligibility. Ask us which
              vehicles currently include an insurance option.
            </p>
            <div className="mt-6">
              <WhatsAppButton message={waMessages.withInsurance()} testid="insurance-with-whatsapp">
                Ask About Insurance-Inclusive
              </WhatsAppButton>
            </div>
          </div>
        </div>
        <p className="mt-8 max-w-3xl text-sm leading-relaxed text-zinc-400">
          Insurance availability, approval and pricing are subject to driver details, licence
          history and insurer requirements. Insurance is not guaranteed.
        </p>
      </div>
    </section>
  );
}

export function DriverRequirements() {
  const items = [
    "Full UK driving licence",
    "Appropriate private hire driver licence",
    "Proof of address",
    "Photo identification",
    "Driving history",
    "Deposit",
    "Suitable insurance or insurance eligibility",
  ];
  return (
    <section className="container-x py-20">
      <div className="grid gap-10 md:grid-cols-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Before You Rent</p>
          <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">What Do I Need to Rent?</h2>
          <p className="mt-4 leading-relaxed text-zinc-600">
            Requirements vary by vehicle and insurance option. Potential requirements may include the
            items listed. We can't promise every driver will qualify — WhatsApp us and we'll explain
            exactly what's needed for the car you want.
          </p>
          <div className="mt-6">
            <WhatsAppButton message={waMessages.requirements()} testid="requirements-whatsapp">
              Not sure? WhatsApp us
            </WhatsAppButton>
          </div>
        </div>
        <ul className="grid gap-3 self-center">
          {items.map((it) => (
            <li key={it} className="flex items-center gap-3 border border-border p-4 text-sm font-semibold">
              <FileCheck className="h-5 w-5 shrink-0 text-[#00E676]" /> {it}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function CtaBanner({ message }) {
  return (
    <section className="container-x py-16">
      <div className="grain flex flex-col items-start gap-6 border border-zinc-900 bg-ink p-10 text-white md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold sm:text-3xl">Ready to get on the road?</h2>
          <p className="mt-2 text-zinc-300">WhatsApp or call us and we'll tell you exactly what's available right now.</p>
        </div>
        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
          <WhatsAppButton size="lg" message={message || waMessages.general()} context="cta-banner" testid="cta-banner-whatsapp">
            WhatsApp Us Now
          </WhatsAppButton>
          <CallButton size="lg" variant="light" context="cta-banner" testid="cta-banner-call">Call Us</CallButton>
        </div>
      </div>
    </section>
  );
}
